import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../homes.css';
import dahboard from '../../../assets/images/darhboard.png';
import MenuItemCard from './menuItemCard';
import './menuItemcard.css';
import AddItembox from './addItems/addItembox';
import ApiService from '../../../services/apiService';
import CategoryNavBar from './categoryNav/categoryNav';
import MenuGrid from './grid/menuGrid';

const Menu = () => {
  const [menu, setMenu] = useState([]);
  const [view, setView] = useState('card');

  // keep same input behavior/UI
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const scrollContainerRef = useRef(null);
  const pageSize = 25;

  // guards to prevent loops / duplicate calls
  const lastRequestKeyRef = useRef('');
  const requestIdRef = useRef(0);
  const appliedTermRef = useRef(''); // the term currently used for pagination
  const initialLoadedRef = useRef(false);

  const toggleView = () => setView(view === 'card' ? 'table' : 'card');

  const GetMenu = useCallback(
    async (pageNumber = 1, isNewSearch = false, term = '') => {
      if (loading) return;

      const cleanTerm = (term || '').trim();
      const reqKey = `${cleanTerm}__${pageNumber}__${pageSize}`;

      // ✅ stop duplicate same request (fixes looping)
      if (lastRequestKeyRef.current === reqKey) return;
      lastRequestKeyRef.current = reqKey;

      const myReqId = ++requestIdRef.current;

      try {
        setLoading(true);

        const apiService = new ApiService();
        // ✅ FIX: API parameter must be ItemName
        const parameters = `?ItemName=${encodeURIComponent(cleanTerm)}&pageNo=${pageNumber}&pageSize=${pageSize}`;

        const res = await apiService.getApiParamater('GET_ITEM_QUICK', parameters);

        // ignore stale responses
        if (myReqId !== requestIdRef.current) return;

        if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
          const newItems = res?.data?.result || [];
          const total = res?.data?.totalCount || 0;

          setTotalItems(total);

          if (isNewSearch || pageNumber === 1) {
            setMenu(newItems);
          } else {
            setMenu((prev) => [...prev, ...newItems]);
          }

          setHasMoreData(newItems.length === pageSize);
        } else if (res?.data?.statusCode === 204) {
          if (isNewSearch || pageNumber === 1) setMenu([]);
          setTotalItems(0);
          setHasMoreData(false);
        } else {
          if (isNewSearch || pageNumber === 1) setMenu([]);
          setHasMoreData(false);
        }
      } catch (error) {
        if (myReqId !== requestIdRef.current) return;
        console.error('Error fetching menu data:', error);
        setHasMoreData(false);
      } finally {
        if (myReqId === requestIdRef.current) setLoading(false);
      }
    },
    [loading, pageSize],
  );

  // ✅ Load ALL items on page load (ItemName empty)
  useEffect(() => {
    if (initialLoadedRef.current) return;
    initialLoadedRef.current = true;

    appliedTermRef.current = '';
    setCurrentPage(1);
    setHasMoreData(true);
    setMenu([]);
    GetMenu(1, true, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ debounce search input (prevents multiple rapid calls)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ✅ when debounced search changes, run ONE new search (page 1)
  useEffect(() => {
    // first load already handled above
    if (!initialLoadedRef.current) return;

    const term = (debouncedTerm || '').trim();

    // If same applied term, don't refetch
    if (appliedTermRef.current === term) return;

    appliedTermRef.current = term;
    lastRequestKeyRef.current = ''; // allow fresh request key
    setCurrentPage(1);
    setHasMoreData(true);
    setMenu([]);
    GetMenu(1, true, term);
  }, [debouncedTerm, GetMenu]);

  // Load more items when scrolling (pagination uses appliedTermRef)
  const loadMoreItems = useCallback(() => {
    if (!hasMoreData || loading) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    GetMenu(nextPage, false, appliedTermRef.current);
  }, [currentPage, hasMoreData, loading, GetMenu]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasMoreData || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 100;

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      loadMoreItems();
    }
  }, [hasMoreData, loading, loadMoreItems]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  return (
    <div className="container-fluid p-lg-5 m-0">
      <div className="pt-3">
        <div className="row">
          <div className="col-lg-6 col-md-6 mbl-center">
            <div className="d-flex gap-4 pt-3">
              {/* ✅ same UI - only logic fixed */}
              <input type="Search" className="form-control w-50" placeholder="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              <img onClick={toggleView} src={dahboard} className="filter" alt="toggle" />
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="d-flex justify-content-end pt-top mbl-center">
              <AddItembox />
            </div>
          </div>
        </div>

        <div>
          <div className="menu">
            {/* ✅ keep design: category navbar stays */}
            <div className="pt-5">
              <CategoryNavBar />
            </div>

            {/* Results counter */}
            {menu.length > 0 && (
              <div className="pt-3">
                <small className="text-muted">
                  Showing {menu.length} of {totalItems} items
                  {loading && ' (Loading...)'}
                </small>
              </div>
            )}

            <div
              className="menu-items pt-5"
              ref={scrollContainerRef}
              style={{
                maxHeight: view === 'card' ? '80vh' : 'auto',
                overflowY: view === 'card' ? 'auto' : 'visible',
              }}
            >
              {view === 'table' && (
                <div className="table-responsive">
                  <MenuGrid val={menu} GetMenu={() => GetMenu(1, true, appliedTermRef.current)} />
                </div>
              )}

              {menu?.length > 0 ? (
                <div className="grid">
                  {view === 'card' &&
                    menu.map((item, itemIndex) => (
                      <MenuItemCard
                        key={`${item.id || item.record_Id}-${itemIndex}`}
                        {...item}
                        item={item}
                        GetMenu={async () => {
                          lastRequestKeyRef.current = '';
                          setCurrentPage(1);
                          setHasMoreData(true);
                          await GetMenu(1, true, appliedTermRef.current);
                        }}
                      />
                    ))}
                </div>
              ) : (
                <div className="center p-lg-5">
                  {loading ? (
                    <div className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5 className="mt-3">Loading items...</h5>
                    </div>
                  ) : (
                    <h5>No Item Found</h5>
                  )}
                </div>
              )}

              {/* Loading indicator for infinite scroll */}
              {loading && menu.length > 0 && (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading more...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading more items...</p>
                </div>
              )}

              {/* End of results indicator */}
              {!hasMoreData && menu.length > 0 && !loading && (
                <div className="text-center py-4">
                  <p className="text-muted">You've reached the end of the list</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Menu;
